import { Button, Table, message, Modal, Drawer, Form, Input, Switch } from 'antd';

import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { CloseOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import qs from 'qs';

import IconDelete from 'assets/images/IconDelete.svg';
import IconEdit from 'assets/images/IconEdit.svg';
import IconNotifyAction from 'assets/images/IconNotifyAction.svg';

//requests
import { getListStudent, deleteStudent, uploadStudents, updateStudent } from 'requests/student';
import { addStudentClass } from 'requests/student';

//Components
import TableAction from 'components/TableAction';
import { sendNotificationToStudents } from 'requests/notification';
import Media from 'react-media';

const { TextArea } = Input;
const { confirm } = Modal;

const StudentPage = () => {
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [loading, setLoading] = useState(true);
    const [records, setRecords] = useState([]);
    const [pageSize, setPageSize] = useState(10);
    const [total, setTotal] = useState(0);
    const [currentQuery, setCurrentQuery] = useState({});
    const [selectedClass, setSelectedClass] = useState(null);
    const [open, setOpen] = useState(false);
    const [sending, setSending] = useState(false);

    const authUser = useSelector((state) => state.auth.authUser);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const [form] = Form.useForm();

    const showDrawer = () => {
        setOpen(true);
        form.setFieldsValue({
            title: '',
            content: '',
        });
    };

    const closeDrawer = () => {
        setOpen(false);
        form.setFieldsValue({
            title: '',
            content: '',
        });
    };

    let columns = [
        {
            title: 'ID',
            dataIndex: 'id',
        },
        {
            title: t('student_name'),
            dataIndex: 'name',
            render: (text, record) => (
                <div>
                    {authUser.role === 1 ? (
                        <Link to={`/students/${record.id}/edit`} type="link">
                            {text}
                        </Link>
                    ) : (
                        <Link to={`/students/${record.id}/view`} type="link">
                            {text}
                        </Link>
                    )}
                </div>
            ),
        },
        {
            title: t('nickname'),
            dataIndex: 'nickname',
        },
        {
            adminOnly: true,
            title: t('teacher_in_charge'),
            dataIndex: 'classes',
            render: (classes) => {
                if (classes[0]?.teachers) {
                    return (
                        <div>
                            {classes[0].teachers.map((e, i) => (
                                <React.Fragment key={i}>
                                    {i > 0 && <span>, </span>}
                                    <Link to={`/teachers/${e.id}/edit`} className="nowrap">
                                        {e.name}
                                    </Link>
                                </React.Fragment>
                            ))}
                        </div>
                    );
                }
            },
        },
        {
            title: t('class'),
            dataIndex: 'classes',
            render: (text, record, index) => (
                <div>
                    {record.classes.map((e, i) => (
                        <React.Fragment key={i}>
                            {i > 0 && <span>, </span>}
                            {authUser.role === 1 ? (
                                <Link to={`/classes/${e.id}/edit`} className="nowrap">
                                    {e.className}
                                </Link>
                            ) : (
                                <Link to={`/classes/${e.id}/list`} className="nowrap">
                                    {e.className}
                                </Link>
                            )}
                        </React.Fragment>
                    ))}
                </div>
            ),
        },
        {
            adminOnly: true,
            title: t('status'),
            dataIndex: 'status',
            render: (status, record, index) => (
                <Switch defaultChecked={!!status} onChange={(checked) => handleChangeStatus(record.id, checked)} />
            ),
        },
        {
            title: t('nationality'),
            dataIndex: 'nationality',
        },
        {
            title: t('birthday'),
            dataIndex: 'birthday',
            render: (birthday) => birthday?.replace(/[-/\\]/g, '/'),
        },
        {
            title: t('mobile'),
            dataIndex: 'mobile',
        },
        {
            title: t('scoreboard'),
            dataIndex: 'name',
            render: (text, record, index) => (
                <Link to={`/students/${record.id}/view`} type="link" className="scoreboard-link">
                    {t('view')}
                </Link>
            ),
        },
        {
            title: t('action'),
            render: (text, record, index) => (
                <div className="nowrap">
                    <a
                        className="mr-12"
                        onClick={() => {
                            showDrawer();
                            setSelectedRowKeys([record.id]);
                        }}
                    >
                        <img src={IconNotifyAction} alt="" />
                    </a>
                    {authUser.role === 1 && (
                        <Link className="mr-12" to={`/students/${record.id}/edit`} type="link">
                            <img src={IconEdit} alt="" />
                        </Link>
                    )}
                    <a onClick={() => handleClickDeleteBtn(record.id)}>
                        <img src={IconDelete} alt="" />
                    </a>
                </div>
            ),
        },
    ];

    useEffect(() => {
        getData();
    }, [location]);

    const getData = async () => {
        try {
            setLoading(true);

            const queryString = location.search.slice(1);
            const query = qs.parse(queryString);
            setCurrentQuery(query);

            const page = query.page || 1;
            const takeCount = query.pageSize || 10;
            const skipCount = (page - 1) * takeCount;

            const data = await getListStudent({ search: query.search, take: takeCount, skip: skipCount });

            if (data.total > 0 && page > Math.ceil(data.total / data.take)) {
                const newQuery = {
                    ...query,
                    page: Math.ceil(data.total / data.take),
                };
                const queryString = qs.stringify(newQuery);

                return navigate({
                    pathname: location.pathname,
                    search: `?${queryString}`,
                });
            }

            setRecords(data.records);
            setPageSize(data.take);
            setTotal(data.total);
        } catch (err) {
            console.log(err);
            message.error(t(err.response?.data.message || 'error'));
        } finally {
            setLoading(false);
        }
    };

    const handleChangeStatus = async (id, checked) => {
        try {
            const data = { status: Number(checked) };
            await updateStudent(id, data);
            // notify success
        } catch (err) {
            console.log(err);
            message.error(t(err.response?.data.message || 'error'));
        }
    };

    const handleClickDeleteBtn = (id) => {
        // modal confirm
        confirm({
            title: t('confirm_delete_one'),
            icon: <ExclamationCircleOutlined />,
            cancelText: t('cancel'),
            onOk() {
                handleDelete(id);
            },
        });
    };

    // delete single record
    const handleDelete = async (id) => {
        try {
            setLoading(true);
            await deleteStudent({ memberIds: [id] });
            getData();
        } catch (err) {
            console.log(err);
            message.error(t(err.response?.data.message || 'error'));
        } finally {
            setLoading(false);
        }
    };

    let handleDeleteMany = async () => {
        try {
            setLoading(true);
            await deleteStudent({ memberIds: selectedRowKeys });
            setSelectedRowKeys([]);
            getData();
        } catch (err) {
            console.log(err);
            message.error(t(err.response?.data.message || 'error'));
        } finally {
            setLoading(false);
        }
    };

    let handleNotify = async (memberIds = selectedRowKeys) => {
        try {
            setSending(true);

            const values = await form.validateFields();
            const result = await sendNotificationToStudents({
                memberIds,
                ...values,
            });

            message.success(`${t('success')}: ${result.success}`);

            setSelectedRowKeys([]);
            closeDrawer();
            getData();
        } catch (err) {
            console.log(err);
            message.error(t(err.response?.data.message || 'error'));
        } finally {
            setSending(false);
        }
    };

    let handleAdd = () => {
        navigate('/students/create');
    };

    let handleUpload = (e) => {
        const file = e.target.files[0];
        const formData = new FormData();
        formData.append('list', file);

        setLoading(true);
        uploadStudents(formData)
            .then(() => {
                message.success(t('success'));

                navigate({
                    pathname: location.pathname,
                });
            })
            .catch((err) => {
                console.log(err);
                const errors = err.response?.data.response;
                if (typeof errors === 'object') {
                    for (const key in errors) {
                        message.error(`${key}: ${t(errors[key])}`);
                    }
                } else {
                    message.error(t(errors));
                }
            })
            .finally(() => {
                setLoading(false);
                e.target.value = '';
            });
    };

    let showClassSelect = async (classId) => {
        try {
            if (!selectedRowKeys.length) return;

            setLoading(true);
            setSelectedClass(classId);
            await addStudentClass(classId, { memberIds: selectedRowKeys });
            getData();

            message.success(t('success'));
            setSelectedRowKeys([]);
        } catch (err) {
            console.log(err);
            message.error(t(err.response?.data.message || 'error'));
        } finally {
            setLoading(false);
            setSelectedClass(null);
        }
    };

    const handleSearch = async (value) => {
        const query = {
            ...currentQuery,
            search: value,
            page: 1,
        };

        const queryString = qs.stringify(query);
        navigate({
            pathname: location.pathname,
            search: `?${queryString}`,
        });
    };

    const handleSelectChange = (newSelectedRowKeys) => {
        setSelectedRowKeys(newSelectedRowKeys);
    };

    const handlePageChange = (page, pageSize) => {
        setSelectedRowKeys([]);

        const query = {
            ...currentQuery,
            page: page,
            pageSize: pageSize,
        };

        const queryString = qs.stringify(query);
        navigate({
            pathname: location.pathname,
            search: `?${queryString}`,
        });
    };

    let rowSelection = {
        selectedRowKeys,
        onChange: handleSelectChange,
    };

    const hasSelected = selectedRowKeys.length > 0;
    if (authUser?.role === 2) {
        columns = columns.filter((e) => !e.adminOnly);
        handleAdd = null;
        // handleDeleteMany = null;
        showClassSelect = null;
        handleUpload = null;
    }

    return (
        <>
            <div className="header-panel">
                <div className="title ml-24">{t('student')}</div>
                <TableAction
                    query={currentQuery}
                    searchPlaceholder={t('search_student')}
                    showClassSelect={authUser?.role === 1}
                    disableDeleteBtn={!hasSelected}
                    onSearch={handleSearch}
                    onDelete={handleDeleteMany}
                    onAdd={handleAdd}
                    onNotify={showDrawer}
                    selectedClass={selectedClass}
                    onChangeClass={showClassSelect}
                    onUpload={handleUpload}
                />
            </div>
            <Media query="(max-width: 1499px)">
                {(matched) => {
                    let mediaColumns = columns;
                    if (matched) mediaColumns = columns.filter((e) => e.dataIndex !== 'mobile');

                    return (
                        <Table
                            rowKey="id"
                            rowSelection={rowSelection}
                            columns={mediaColumns}
                            dataSource={records}
                            loading={loading}
                            pagination={{
                                pageSize: pageSize,
                                total: total,
                                showSizeChanger: true,
                                showQuickJumper: true,
                                current: Number(currentQuery.page) || 1,
                                onChange: (page, pageSize) => handlePageChange(page, pageSize),
                            }}
                        />
                    );
                }}
            </Media>
            <Drawer
                className="notification-box"
                title={t('send_notification')}
                width={520}
                onClose={null}
                closable={false}
                visible={open}
            >
                <div className="close-icon">
                    <CloseOutlined style={{ fontSize: 24, lineHeight: '55px' }} onClick={closeDrawer} />
                </div>
                <Form form={form} labelCol={{ span: 4 }} wrapperCol={{ span: 20 }}>
                    <Form.Item
                        label={t('title')}
                        name="title"
                        rules={[{ required: true, message: t('required_field') }]}
                    >
                        <Input placeholder={t('input_title')} />
                    </Form.Item>
                    <Form.Item
                        label={t('content')}
                        name="content"
                        rules={[{ required: true, message: t('required_field') }]}
                    >
                        <TextArea
                            autoFocus={true}
                            placeholder={t('input_content')}
                            rows={3}
                            style={{ resize: 'none' }}
                        />
                    </Form.Item>
                </Form>

                <div className="form-btn">
                    <Button onClick={closeDrawer}>{t('cancel')}</Button>
                    <Button className="ml-12" loading={sending} onClick={() => handleNotify()} type="primary">
                        {t('send')}
                    </Button>
                </div>
            </Drawer>
        </>
    );
};

export default StudentPage;
